// src/public-routes/sectors/HospitalitySector.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Progress, Badge } from 'reactstrap';
import { Link } from 'react-router-dom';
import {
    FaHotel,
    FaSearch,
    FaBullseye,
    FaStar,
    FaUsers,
    FaCalendarCheck,
    FaChartLine,
    FaGlobe,
    FaTrophy
} from 'react-icons/fa';

const HospitalitySector = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [loadingPercent, setLoadingPercent] = useState(0);
    const loadingRef = useRef(null);

    // Handle percentage-based loading
    useEffect(() => {
        window.scrollTo(0, 0);
        if (!isLoading) return;

        const interval = setInterval(() => {
            setLoadingPercent(prev => {
                const newPercent = Math.min(prev + 5, 100);

                if (newPercent === 100) {
                    clearInterval(interval);
                    setTimeout(() => {
                        setIsLoading(false);
                        window.scrollTo(0, 0);
                    }, 500);
                }

                return newPercent;
            });
        }, 100);

        return () => clearInterval(interval);
    }, [isLoading]);

    const stats = [
        { number: '72%', label: 'of bookings are made online' },
        { number: '3.5x', label: 'more direct bookings with optimized websites' },
        { number: '68%', label: 'of travelers research online before booking' },
        { number: '45%', label: 'reduction in OTA commission dependency' }
    ];

    const approaches = [
        {
            title: "Direct Booking Optimization",
            description: "Reduce reliance on third-party platforms with SEO, PPC, and conversion strategies that drive direct reservations through your website.",
            icon: FaCalendarCheck
        },
        {
            title: "Brand Visibility & Reputation",
            description: "Build a strong online presence across search, social, and review platforms that attracts guests and builds trust before they book.",
            icon: FaStar
        },
        {
            title: "Guest Experience Marketing",
            description: "Create personalized digital journeys that showcase your property's unique experience and convert browsers into bookings.",
            icon: FaUsers
        },
        {
            title: "Revenue Management Marketing",
            description: "Align marketing campaigns with revenue goals through seasonal promotions, packages, and targeted offers that maximize occupancy.",
            icon: FaChartLine
        }
    ];

    const benefits = [
        ['Increased Direct Bookings', 'Reduce OTA commission fees by driving more reservations through your own channels'],
        ['Stronger Brand Presence', 'Build recognition and trust that sets your property apart from competitors'],
        ['Higher Guest Loyalty', 'Create personalized experiences that encourage repeat visits and referrals'],
        ['Revenue Optimization', 'Maximize occupancy rates and RevPAR through targeted, seasonal campaigns'],
        ['Competitive Differentiation', 'Showcase your unique amenities and experiences that OTAs cannot replicate']
    ];

    const questions = [
        'How can you reduce dependency on OTAs and increase direct bookings?',
        'What strategies will help your property stand out in a crowded market?',
        'How do you turn guest experiences into measurable business growth?',
        'Which digital channels deliver the highest ROI for hotels and resorts?',
        'How can you build guest loyalty in an experience-driven market?'
    ];

    return (
        <>


            <div className="hospitality-sector-page st-page">
                {/* Top Image Section */}
                <Row xl='1'>
                    <Col xl='12' >
                        <div className="st-decorative-image-container">
                            <img
                                src={require('./Hospitality.PNG')}
                                alt="Hospitality Digital Marketing - Increase Direct Bookings"
                                loading="eager"
                                className='sector-img'
                            />
                        </div>

                    </Col>
                    <Col> {/* Title Section */}
                        <div className="sv-page-title-section">
                            <div className="container">
                                <h1>
                                    <span className="sv-gradient-text">Hospitality</span> Digital Marketing
                                </h1>
                                <p>Reduce reliance on third-party platforms and increase direct bookings with tailored digital strategies</p>
                            </div>
                        </div></Col>
                </Row>

                {/* Main Content */}
                <div className="container-fluid sv-content-wrapper">
                    <div className="sv-main-content">
                        {/* Hero Section */}
                        <section className="sv-hero-section" data-animated>
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <p className="sv-lead">
                                        In the competitive <span className="sv-keyword-highlight">hospitality industry</span>, we help brands reduce reliance on third-party platforms and increase direct bookings with tailored digital strategies that elevate online presence.
                                    </p>
                                    <p className="sv-description">
                                        At Reachtix, our <strong>hospitality digital marketing services</strong> combine strategic thinking, data intelligence, and advanced tools to create campaigns that showcase your property's unique experience. We help hospitality brands turn guest experiences into measurable business growth.
                                    </p>
                                    <p className="sv-description">
                                        We understand the unique challenges hospitality brands face: OTA dependency, seasonal demand fluctuations, reputation management, and the need to create seamless digital experiences that inspire and convert potential guests.
                                    </p>
                                    <div className="sv-cta-buttons">
                                        <Button tag={Link} to="/contact" color="dark">
                                            <i className="fas fa-rocket mr-2"></i>
                                            Start Hospitality Strategy
                                        </Button>
                                        <Button tag={Link} to="/contact" color="dark">
                                            <i className="fas fa-comments mr-2"></i>
                                            Get Free Consultation
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </section>

                        {/* Stats Section */}
                        <section className="sv-stats-section">
                            <h2 className="sv-section-title" data-animated>
                                Why <span className="sv-text-gradient">Hospitality Marketing</span> Matters
                            </h2>
                            <Row className="sv-stats-grid">
                                {stats.map((stat, index) => (
                                    <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                        <div className="sv-stat-card">
                                            <div className="sv-stat-number">{stat.number}</div>
                                            <div className="sv-stat-label">{stat.label}</div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </section>

                        {/* What We Do Section */}
                        <section className="sv-explanation-section" data-animated>
                            <Row>
                                <Col lg={8}>
                                    <h2 className="sv-section-title">What We Do for Hospitality Brands</h2>
                                    <p className="sv-section-description">
                                        <strong>Hospitality digital marketing</strong> goes beyond just promoting rooms. It's about creating compelling digital experiences that showcase your property's unique atmosphere, build trust with potential guests, and drive direct bookings that maximize revenue.
                                    </p>
                                    <div className="sv-questions-grid">
                                        <h4>Key Questions We Answer:</h4>
                                        {questions.map((question, index) => (
                                            <div key={index} className="sv-question-item">
                                                <i className="fas fa-question-circle"></i>
                                                {question}
                                            </div>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                        </section>

                        {/* Benefits Overview */}
                        <section className="sv-benefits-overview" data-animated>
                            <Row>
                                <Col xl={12}>
                                    <h2 className="sv-section-title">Why Invest in Hospitality Marketing?</h2>
                                    <p className="sv-section-description">
                                        The hospitality landscape is evolving rapidly. A comprehensive digital marketing strategy is essential to reduce OTA dependency, build direct guest relationships, and create a sustainable booking engine that drives consistent revenue.
                                    </p>
                                    <div className="sv-benefits-cards">
                                        <div className="sv-benefit-card">
                                            <i className="fas fa-calendar-check"></i>
                                            <h5>Direct Bookings</h5>
                                            <p>Drive reservations through your website and reduce OTA commission costs</p>
                                        </div>
                                        <div className="sv-benefit-card">
                                            <i className="fas fa-star"></i>
                                            <h5>Brand Authority</h5>
                                            <p>Build trust and recognition that sets your property apart</p>
                                        </div>
                                        <div className="sv-benefit-card">
                                            <i className="fas fa-users"></i>
                                            <h5>Guest Loyalty</h5>
                                            <p>Create personalized experiences that encourage repeat visits</p>
                                        </div>
                                        <div className="sv-benefit-card">
                                            <i className="fas fa-chart-line"></i>
                                            <h5>Revenue Growth</h5>
                                            <p>Maximize occupancy and RevPAR with targeted campaigns</p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </section>

                        {/* Our Approach Section */}
                        <section className="sv-approach-section" data-animated>
                            <Row>
                                <Col>
                                    <h2 className="sv-section-title">Our Hospitality Marketing Services</h2>
                                    <p className="sv-section-description">
                                        Reachtix offers a comprehensive suite of hospitality marketing services designed to elevate your online presence, attract qualified guests, and turn guest experiences into measurable business growth.
                                    </p>

                                    <Row className="sv-services-grid">
                                        {approaches.map((service, index) => (
                                            <Col lg={6} md={6} key={index} className="mb-4">
                                                <div className="sv-approach-card p-4 h-100">
                                                    <div className="sv-approach-icon">
                                                        <service.icon />
                                                    </div>
                                                    <div className="sv-approach-content">
                                                        <h4>{service.title}</h4>
                                                        <p className="service-description">{service.description}</p>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </Col>
                            </Row>
                        </section>

                        {/* Benefits Table Section */}
                        <section className="sv-benefits-section" data-animated>
                            <Row>
                                <Col>
                                    <h2 className="mt-5 sv-section-title">Key Benefits of Hospitality Marketing With Reachtix</h2>
                                    <div className="table-responsive">
                                        <table className="table sv-benefits-table">
                                            <thead>
                                                <tr>
                                                    <th>Core Advantage</th>
                                                    <th>Business Impact</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {benefits.map(([advantage, impact], index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <i className="fas fa-check-circle"></i>
                                                            <strong>{advantage}</strong>
                                                        </td>
                                                        <td>{impact}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Col>
                            </Row>
                        </section>

                        {/* Main CTA Section */}
                        <section className="sv-main-cta" data-animated>
                            <Row>
                                <Col>
                                    <h2>Ready to Transform Your Hospitality Business?</h2>
                                    <p className="sv-cta-description">
                                        Whether you're an independent hotel, boutique property, or hospitality group, our comprehensive digital marketing services provide the expertise and results you need to increase direct bookings and build lasting guest relationships.
                                    </p>
                                    <p className="sv-cta-description">
                                        Let's build a digital strategy that showcases your unique experience and drives measurable revenue growth.
                                    </p>
                                    <div className="sv-cta-buttons">
                                        <Button tag={Link} to="/contact" color="primary">
                                            <i className="fas fa-calendar-check mr-2"></i>
                                            Start Hospitality Consultation
                                        </Button>
                                        <Button tag={Link} to="/contact" color="light">
                                            <i className="fas fa-clipboard-list mr-2"></i>
                                            Get Property Audit
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </section>
                    </div>
                </div>
            </div>
        </>
    );
};

export default HospitalitySector;